import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useAdminAccess } from '@/hooks/access'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { LogSmsPush } from '@/modules/admin/resources'

import { SMSLogsListItem } from './SMSLogsListItem'

export const SMSLogsList: FC<{
  searchFilter: string
  logs: LogSmsPush[]
  onClick: (log: LogSmsPush) => void
}> = ({ searchFilter, logs, onClick }) => {
  useAdminAccess()
  const { locale } = useAppContext()
  const { isLoading, isError } = useQuery(queries.adminSMSLogsList(), adminResources.getSMSLogsList)
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof LogSmsPush>()

  const sortedList = getSortedList({ list: logs, querySort, querySortBy, locale })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || logs.length === 0) {
    return <NoResults />
  }

  return (
    <>
      <ResultCount count={logs.length} />
      <Table className="table-fixed">
        <thead>
          <tr>
            <Th id="sent" onClick={handleSort} className="hidden w-2/12 md:table-cell">
              <Translation id="date" />
            </Th>
            <Th id="sender" onClick={handleSort} className="hidden w-2/12 md:table-cell">
              <Translation id="sender" />
            </Th>

            <Th id="receivers" onClick={handleSort} className="hidden w-1/12 md:table-cell">
              <Translation id="receivers" />
            </Th>

            <Th id="type" onClick={handleSort} className="hidden w-2/12 md:table-cell">
              <Translation id="type" />
            </Th>

            <Th id="message" onClick={handleSort} className="w-5/12">
              <Translation id="message" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <SMSLogsListItem
              key={item.id}
              searchFilter={searchFilter}
              onClick={() => item.batchId && onClick(item)}
              {...item}
            />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}
